/**
 * Lifecycle API client (data-export, per-child consent, terms-version).
 *
 * (Family-deletion / erasure endpoints live in ``../erasure/api.ts``.)
 *
 * The 5-minute auth challenge is required for the destructive paths;
 * ``data-export/request`` inherits the same gate so an attacker can't
 * trigger an emailed export against a stolen session.
 */

import { authFetch, request } from "../api";
import type { ExportRequest, RevokeConsentResult } from "./types";

// ─── Auth challenge ──────────────────────────────────────────────────────

export async function authChallenge(password: string): Promise<{ status: string; valid_for_seconds: string }> {
  return request("/family/auth_challenge", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// ─── Data export (async — preferred) ─────────────────────────────────────

export async function requestAsyncExport(): Promise<{ id: string; status: string }> {
  return request("/family/data-export/request", { method: "POST" });
}

export async function listRecentExports(): Promise<{ exports: ExportRequest[] }> {
  return request("/family/data-export/recent", { method: "GET" });
}

/** Synchronous JSON export (fallback path; not exposed in the new card). */
export async function fetchSyncExport(download = false): Promise<Response> {
  const url = `/family/data-export${download ? "?download=1" : ""}`;
  // The request helper unwraps JSON; for a download we want the raw Response.
  return authFetch(url);
}

/** Raw structured export — feeds the inline review surface. Requires a
 *  fresh /family/auth_challenge (5-minute window). The shape mirrors
 *  ``build_family_export`` in backend/app/services/data_review.py: every
 *  top-level key is a list of raw column dicts. */
export interface RawFamilyExport {
  tenant_id: string;
  users: Array<Record<string, unknown>>;
  child_details: Array<Record<string, unknown>>;
  projects: Array<Record<string, unknown>>;
  project_versions: Array<Record<string, unknown>>;
  project_assets: Array<Record<string, unknown>>;
  conversation_turns: Array<Record<string, unknown>>;
  voice_sessions: Array<Record<string, unknown>>;
  avatars: Array<Record<string, unknown>>;
  avatar_characters: Array<Record<string, unknown>>;
  friendships: Array<Record<string, unknown>>;
  friendship_requests: Array<Record<string, unknown>>;
  permission_requests: Array<Record<string, unknown>>;
  parent_verification_sessions: Array<Record<string, unknown>>;
  subscriptions: Array<Record<string, unknown>>;
}

export async function fetchDataReviewExport(): Promise<RawFamilyExport> {
  return request<RawFamilyExport>("/family/data-export", { method: "GET" });
}

// ─── Per-child consent ──────────────────────────────────────────────────

export async function revokeChildConsent(
  child_id: string,
  password: string,
): Promise<RevokeConsentResult> {
  return request(`/family/children/${child_id}/revoke_consent`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function grantChildConsent(child_id: string): Promise<{ status: "restored" }> {
  return request(`/family/children/${child_id}/grant_consent`, {
    method: "POST",
  });
}

// setChildDirectoryOptin removed — findability is driven by the existing
// privacy preset + Fine-tune ``inbound_friend_requests`` setting (synced
// to ``friendship_permission``). There is no separate per-child directory
// API call; ``PUT /privacy/child/{id}/fine-grained`` handles it.

// ─── Terms acceptance ────────────────────────────────────────────────────

export async function acceptTerms(terms_version: number): Promise<unknown> {
  return request("/billing/accept_terms", {
    method: "POST",
    body: JSON.stringify({ terms_version }),
  });
}
